import React, { useState, useEffect } from "react";
import { IDeviceModel } from "./CompanyPage";
import AddDeviceModal from "../../components/modal/AddDeviceModal";
import EditDeviceModal from "../../components/modal/EditDeviceModal";
import DeviceMenu from "../device/DeviceMenu";
import { CpuChipIcon } from "@heroicons/react/24/outline";
import { getPreSignedUrlForDownload } from "../../api/admin";

interface CompanyDeviceImageProps {
  device: IDeviceModel;
}

const CompanyDeviceImage: React.FC<CompanyDeviceImageProps> = ({ device }) => {
  const [imageUrl, setImageUrl] = useState<string>();

  useEffect(() => {
    if (!device.imageKey) {
      return;
    }
    const fetchUrl = async () => {
      try {
        const res = await getPreSignedUrlForDownload({ key: device.imageKey! });
        setImageUrl(res.url);
      } catch (error) {
        console.error("Failed to fetch device image:", error);
      }
    };

    fetchUrl();
  }, [device.imageKey]);

  if (imageUrl) {
    return (
      <img
        className="w-8 h-8 rounded object-cover"
        src={imageUrl}
        alt={`${device.name} device`}
      />
    );
  }

  return null;
};

const CompanyDevice = ({ deviceList }: { deviceList: IDeviceModel[] }) => {
  const [dialog, setDialog] = useState(false);
  const [showDeviceId, setShowDeviceId] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState<IDeviceModel | null>(null);

  const handleEditDevice = (device: IDeviceModel) => {
    setDeviceToEdit(device);
    setShowEditModal(true);
  };

  return (
    <div className="px-4 sm:px-6 col-span-2 lg:px-8 dark:bg-[#111B21] pt-6 border border-borderColor shadow-md rounded-md">
      {dialog && (
        <AddDeviceModal
          isOpen={dialog}
          onClose={() => {
            setDialog(false);
          }}
        />
      )}

      {showEditModal && (
        <EditDeviceModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setDeviceToEdit(null);
          }}
          device={deviceToEdit}
        />
      )}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
            Device Subscribed
          </h1>
          <div className="flex items-center ">
            <input
              id="showDeviceId"
              name="showDeviceId"
              type="checkbox"
              checked={showDeviceId}
              onChange={() => setShowDeviceId(!showDeviceId)}
              className="h-4 w-4 rounded cursor-pointer border-gray-300 text-indigo-600 border-2 focus:ring-indigo-600"
            />
            <label
              htmlFor="showDeviceId"
              className="ml-3 block text-sm cursor-pointer leading-6 text-gray-900 dark:text-gray-300"
            >
              Show Device Id
            </label>
          </div>
        </div>

        {
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={() => {
                setDialog(true);
              }}
              className="block cursor-pointer rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Add Device
            </button>
          </div>
        }
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-6 lg:pl-8"
                  >
                    Device Name
                  </th>
                  {showDeviceId && (
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                    >
                      Device ID
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="relative py-3.5 pl-3 pr-4 sm:pr-6 lg:pr-8"
                  >
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700  dark:bg-[#111B21] overflow-auto">
                {deviceList.map((device) => (
                  <tr key={device.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-white sm:pl-6 lg:pl-8">
                      <div className="flex items-center gap-3">
                        {device.imageKey ? (
                          <CompanyDeviceImage device={device} />
                        ) : (
                          <CpuChipIcon className="w-6 h-6 text-gray-400" />
                        )}
                        <span>{device.name}</span>
                      </div>
                    </td>
                    {showDeviceId && (
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-300">
                        {device.id}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-300">
                      {device.modelType}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-300">
                      {device.status}
                    </td>
                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 lg:pr-8">
                      <DeviceMenu
                        menu={[
                          {
                            name: "Block",
                            action: () => {},
                          },
                          {
                            name: "Remove",
                            action: () => {},
                          },
                        ]}
                        onEdit={() => handleEditDevice(device)}
                        onDelete={() => {}}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDevice;
